/**
 * Service de partage unifié
 * Centralise toutes les fonctionnalités de partage : 
 * - Partage simple (texte/URL)
 * - Partage avec image (Median webScreenshot)
 * - Partage LinkedIn/X avec texte pré-rempli
 * - Copie presse-papier
 * - Téléchargement image
 * 
 * @version 1.0.0
 */

import Median from 'median-js-bridge';
import { isMedianNative } from '@/utils/platformDetection';
import { isNativeShareAvailable, shareImageNatively, shareTextNatively } from '@/services/nativeShare';
import { ensureHtml2canvasGlobal } from '@/utils/imageUtils';

// ============== Types ==============

export interface SimpleShareOptions {
  title?: string;
  text?: string;
  url?: string;
}

export interface ImageShareOptions {
  text?: string;
  url?: string;
}

export interface SocialShareData {
  title: string;
  description: string;
  url?: string;
  hashtags?: string[];
}

// ============== Service ==============

/**
 * Vrai seulement dans l'ancienne coque Median, la seule où une commande
 * Median.* a un effet. Dans les coques maison, où isMedianNative() est vraie
 * aussi, la commande ne poste rien et ne lève rien : on croyait avoir partagé
 * alors que rien ne s'ouvrait.
 */
const hasMedianBridge = (): boolean => Median.isNativeApp();

/**
 * Lance la copie sans l'attendre et rend une promesse qui n'échoue jamais.
 * La coque iOS maison n'ouvre une fenêtre que pendant le geste : attendre la
 * copie avant window.open peut le faire perdre, et le lien ne s'ouvre pas.
 * L'appelant ouvre donc le lien dans le même tour, puis attend cette promesse.
 */
const startClipboardCopy = (text: string): Promise<void> => {
  try {
    return navigator.clipboard.writeText(text).catch((error: unknown) => {
      console.warn('[UnifiedShareService] Clipboard copy failed:', error);
    });
  } catch (error) {
    // Pas de presse-papier du tout : l'appel lève tout de suite.
    console.warn('[UnifiedShareService] Clipboard copy failed:', error);
    return Promise.resolve();
  }
};

export const UnifiedShareService = {
  // ============== Detection ==============

  /**
   * Vérifie si on est dans un environnement Median natif
   */
  isNative(): boolean {
    return isMedianNative();
  },

  /**
   * Vrai quand un pont natif sait ouvrir la feuille de partage avec une image
   * (pont maison, ou ancienne coque Median).
   */
  isNativeImageShareAvailable(): boolean {
    return isNativeShareAvailable() || hasMedianBridge();
  },

  // ============== Simple Share (URL/Text) ==============

  /**
   * Partage simple via dialog natif ou Web Share API
   * Fallback vers copie presse-papier
   * @returns true si une feuille de partage s'est ouverte (ou si le pont
   * maison n'a pas répondu dans son délai : rien n'est alors copié ni annoncé),
   * false si le lien a été copié ; lève si rien n'a été possible.
   */
  async shareSimple(options: SimpleShareOptions): Promise<boolean> {
    const url = options.url || window.location.href;
    const text = options.text || options.title || '';

    // 1. Ancienne coque Median
    if (hasMedianBridge()) {
      try {
        Median.share.sharePage({ url, text });
        return true;
      } catch (error) {
        console.warn('[UnifiedShareService] Median share failed:', error);
      }
    }

    if (isNativeShareAvailable()) {
      // 2. Coques maison : le pont natif répond pour de bon. La feuille s'est
      // ouverte, qu'elle ait servi ou qu'elle ait été refermée : rien à annoncer.
      try {
        await shareTextNatively([text, url].filter(Boolean).join('\n\n'));
        return true;
      } catch (error) {
        // Délai dépassé sans réponse. Sur iPhone la coque ne répond qu'à la
        // fermeture de la feuille : elle est sans doute encore ouverte. Copier
        // le lien et l'annoncer à ce moment-là tomberait par-dessus elle.
        if ((error as Error)?.message === 'native_share_timeout') return true;
        // Pas de navigator.share derrière : on ne rouvre pas une seconde feuille.
        console.warn('[UnifiedShareService] Native share failed:', error);
      }
    } else if (navigator.share) {
      // 3. Web Share API
      try {
        await navigator.share({ title: options.title, text, url });
        return true;
      } catch (error) {
        // Feuille refermée par la personne : rien à copier ni à annoncer.
        if ((error as Error)?.name === 'AbortError') return true;
        console.warn('[UnifiedShareService] Web share failed:', error);
      }
    }

    // 4. Repli : copie du lien. Sans try/catch : rendre false ici faisait
    // afficher « Lien copié ! » alors que rien n'était copié.
    await navigator.clipboard.writeText(url);
    return false;
  },

  // ============== Image Share ==============

  /**
   * Partage un élément HTML avec image+texte par la feuille de partage.
   * @returns true si l'image est partie ou si la feuille s'est ouverte sans
   * retour possible (Android, Median), false si la personne a refermé la
   * feuille ; lève si aucun chemin n'existe.
   */
  async shareWithImage(element: HTMLElement, options: ImageShareOptions = {}): Promise<boolean> {
    const legende = [options.text, options.url || 'https://kokenapp.com'].filter(Boolean).join('\n\n');

    // CHEMIN 1 — notre pont natif maison, prioritaire.
    // L'implémentation Median ci-dessous est un no-op SILENCIEUX quand
    // `window.html2canvas` manque : elle ne lève rien, ne retourne rien, et
    // l'appelant affichait « Partagé ! » sans qu'aucune feuille ne s'ouvre.
    // Notre pont, lui, répond toujours — et son échec est un vrai échec.
    if (isNativeShareAvailable()) {
      const { domToCanvas } = await import('modern-screenshot');
      const canvas = await domToCanvas(element, { scale: 2, backgroundColor: null });
      const { shared } = await shareImageNatively({
        dataUrl: canvas.toDataURL('image/png'),
        text: legende,
      });
      // Sur iPhone, false veut dire feuille refermée sans partage.
      return shared;
    }

    // CHEMIN 2 — ancien pont Median, conservé pour les binaires déjà
    // installés qui n'embarquent pas encore le plugin maison.
    if (hasMedianBridge()) {
      // Sans html2canvas en global la commande Median ne fait rien et ne dit rien.
      await ensureHtml2canvasGlobal();
      Median.webScreenshot.shareElement(element, {
        text: options.text || '',
        url: options.url || 'https://kokenapp.com'
      });
      return true;
    }

    // CHEMIN 3, navigateur : un vrai fichier PNG dans la feuille de partage,
    // quand le navigateur sait partager un fichier.
    // La question est posée avec un fichier vide, AVANT la capture : sinon un
    // navigateur qui ne sait pas partager un fichier payait une capture
    // entière pour rien, et le repli de l'appelant arrivait d'autant plus tard.
    const sonde = new File([''], 'koken.png', { type: 'image/png' });
    if (!navigator.share || !navigator.canShare || !navigator.canShare({ files: [sonde] })) {
      throw new Error('Native image share not available');
    }

    const { domToBlob } = await import('modern-screenshot');
    const blob = await domToBlob(element, { scale: 2, backgroundColor: null });
    if (!blob) throw new Error('Native image share not available');
    const file = new File([blob], 'koken.png', { type: 'image/png' });
    try {
      await navigator.share({ files: [file], text: legende });
      return true;
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') return false;
      throw error;
    }
  },

  /**
   * Capture un élément HTML en tant que Blob (pour téléchargement/partage fichier)
   */
  async captureElement(element: HTMLElement): Promise<Blob | null> {
    if (!isMedianNative()) return null;
    
    try {
      const blob = await Median.webScreenshot.captureElement(element);
      return blob;
    } catch (error) {
      console.warn('[UnifiedShareService] captureElement failed:', error);
      return null;
    }
  },

  // ============== Social Links ==============

  /**
   * Ouvre une URL hors de l'app (LinkedIn, X, etc.)
   * Le mode 'external' de Median n'existe que dans l'ancienne coque Median.
   * Dans les coques maison c'est window.open qui sort de l'app : la coque
   * intercepte la nouvelle fenêtre et confie le lien au système.
   */
  openSocialLink(url: string): void {
    if (hasMedianBridge()) {
      try {
        Median.window.open(url, 'external');
        return;
      } catch (e) {
        console.warn('[UnifiedShareService] Median window.open failed, fallback:', e);
      }
    }
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=600');
  },

  /**
   * Partage vers LinkedIn avec texte pré-rempli
   */
  async shareToLinkedIn(data: SocialShareData): Promise<void> {
    const fullText = this.formatShareText(data);
    
    if (this.isNative()) {
      // Sur Median natif : copier le texte + ouvrir share-offsite dans l'app native.
      // La copie part d'abord mais n'est attendue qu'une fois le lien ouvert.
      const copie = startClipboardCopy(fullText);
      const url = encodeURIComponent(data.url || 'https://kokenapp.com');
      const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
      this.openSocialLink(linkedInUrl);
      await copie;
    } else {
      // Sur web/PWA : utiliser feed/?shareActive=true&text=... pour pré-remplir le texte
      const text = encodeURIComponent(fullText);
      const linkedInUrl = `https://www.linkedin.com/feed/?shareActive=true&text=${text}`;
      window.open(linkedInUrl, '_blank', 'noopener,noreferrer,width=600,height=600');
    }
  },

  /**
   * Partage vers X (Twitter) avec texte pré-rempli
   */
  async shareToX(data: SocialShareData): Promise<void> {
    const fullText = this.formatShareText(data);
    
    // Copier le texte dans le presse-papier (backup). La copie part d'abord
    // mais n'est attendue qu'une fois le lien ouvert.
    const copie = startClipboardCopy(fullText);

    // Ouvrir X avec texte pré-rempli
    const text = encodeURIComponent(fullText);
    const xUrl = `https://twitter.com/intent/tweet?text=${text}`;

    if (this.isNative()) {
      this.openSocialLink(xUrl);
    } else {
      window.open(xUrl, '_blank', 'noopener,noreferrer,width=600,height=400');
    }

    await copie;
  },

  // ============== Clipboard ==============

  /**
   * Copie le texte de partage dans le presse-papier
   */
  async copyShareText(data: SocialShareData): Promise<boolean> {
    const text = this.formatShareText(data);
    
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      console.error('[UnifiedShareService] Copy failed:', error);
      return false;
    }
  },

  // ============== Download ==============

  /**
   * Télécharge une image depuis un canvas
   */
  async downloadFromCanvas(canvas: HTMLCanvasElement, filename: string): Promise<boolean> {
    try {
      const link = document.createElement('a');
      link.download = filename;
      link.href = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return true;
    } catch (error) {
      console.error('[UnifiedShareService] Download failed:', error);
      return false;
    }
  },

  /**
   * Télécharge une image depuis un Blob
   */
  async downloadFromBlob(blob: Blob, filename: string): Promise<boolean> {
    try {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = filename;
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error('[UnifiedShareService] Download failed:', error);
      return false;
    }
  },

  /**
   * Partage un fichier via Web Share API (mobile)
   */
  async shareFile(file: File): Promise<boolean> {
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file] });
        return true;
      } catch (error) {
        console.warn('[UnifiedShareService] File share failed:', error);
        return false;
      }
    }
    return false;
  },

  // ============== Helpers ==============

  /**
   * Formate le texte de partage avec hashtags
   */
  formatShareText(data: SocialShareData): string {
    const parts = [
      data.title,
      data.description ? `\n\n${data.description}` : '',
      data.hashtags?.length ? `\n\n${data.hashtags.map(h => `#${h}`).join(' ')}` : '',
      `\n\n🔗 ${data.url || 'https://kokenapp.com'}`
    ];
    return parts.filter(Boolean).join('');
  }
};

// ============== Legacy Exports (Rétrocompatibilité) ==============

/**
 * @deprecated Use UnifiedShareService instead
 */
export const ShareService = {
  openSocialLink: UnifiedShareService.openSocialLink.bind(UnifiedShareService),
  shareElement: UnifiedShareService.shareWithImage.bind(UnifiedShareService),
  isNativeShareAvailable: UnifiedShareService.isNativeImageShareAvailable.bind(UnifiedShareService),
  isNative: UnifiedShareService.isNative.bind(UnifiedShareService)
};
